import { useImperativeHandle, type Ref } from 'react';

import { Anchor } from 'lucide-react';

import { isWinchMovementFeedbackKind } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.helpers.ts';
import type { CameraMovementFeedbackKind } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.types.ts';

import {
  ICON_STROKE,
  SINGLE_ICON_MOVEMENT_FEEDBACK,
  SINGLE_ICON_SIZE,
  WINCH_ANCHOR_SIZE,
  WINCH_ARROW_SIZE,
  WINCH_DIRECTION_ICONS,
} from './cameraMovementFeedback/cameraMovementFeedback.constants.ts';
import {
  IconPulse,
  OverlayRoot,
  PlacementHost,
  WinchIconCombo,
} from './cameraMovementFeedback/CameraMovementFeedbackOverlay.style.ts';
import { useCameraMovementFeedback } from './cameraMovementFeedback/useCameraMovementFeedback.ts';

export type CameraMovementFeedbackOverlayHandle = {
  trigger: (kind: CameraMovementFeedbackKind) => void;
};

type CameraMovementFeedbackOverlayProps = {
  ref?: Ref<CameraMovementFeedbackOverlayHandle>;
  zIndex: number;
};

const FeedbackIconContent = ({ kind }: { kind: CameraMovementFeedbackKind }) => {
  if (isWinchMovementFeedbackKind(kind)) {
    const DirectionIcon = WINCH_DIRECTION_ICONS[kind];
    return (
      <WinchIconCombo $kind={kind}>
        <DirectionIcon size={WINCH_ARROW_SIZE} strokeWidth={ICON_STROKE} />
        <Anchor size={WINCH_ANCHOR_SIZE} aria-hidden strokeWidth={ICON_STROKE} />
      </WinchIconCombo>
    );
  }

  const Icon = SINGLE_ICON_MOVEMENT_FEEDBACK[kind];
  return <Icon size={SINGLE_ICON_SIZE} strokeWidth={ICON_STROKE} />;
};

/** Brief icon flash on the stream after each successful camera/winch/zoom POST. */
export const CameraMovementFeedbackOverlay = ({
  ref,
  zIndex,
}: CameraMovementFeedbackOverlayProps) => {
  const { pulses, trigger } = useCameraMovementFeedback();
  useImperativeHandle(ref, () => ({ trigger }), [trigger]);

  const activePulses = Object.values(pulses);
  if (!activePulses.length) return null;

  return (
    <OverlayRoot $zIndex={zIndex} aria-hidden>
      {activePulses.map(pulse => (
        <PlacementHost key={pulse.kind} $kind={pulse.kind}>
          <IconPulse key={pulse.id}>
            <FeedbackIconContent kind={pulse.kind} />
          </IconPulse>
        </PlacementHost>
      ))}
    </OverlayRoot>
  );
};
